import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { ModalConfirm } from '../../../components/common/ModalConfirm';
import RequiredMark from '../../../components/common/RequiredMark';
import { useDispatch, useSelector } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { PcActivationAction } from '../../../actions/PcActivationAction';
import PCActivationHistoryTable from '../../../components/license/PCActivationHistoryTable';
import { ORG_HQ } from '../../../config/appConstants';
import { Translate as t } from '../../../config/Translate';
import { isPcActivationEnabled, resolveLicenseProduct } from '../../../lib/licenseProduct';
// IMP-424: formatCompanyInlineLabel 의 SSoT 는 licenseHelper (CompanyCell 은 re-export) —
// 같은 개념을 두 경로로 import 하지 않도록 licenseHelper 에서 직접 가져온다.
import { formatCompanyFallbackId, formatCompanyInlineLabel, formatLicenseDate, getFriendlyName, getLicenseUserId, getUserEmail, isExpired, isRenewalLicense, MEMO_MAX_LEN, maskPcGuid } from '../../../lib/licenseHelper';
// IMP-421 (#356, PR #542 R2 리뷰 Major): PC 비활성화 실패 문구도 같은 SSoT 를 쓴다 —
// 종전에는 reason 원문(`STALE_USED_LIST` 등)이 이 화면에만 남아 있었다.
import { resolvePcFailText } from '../../../lib/pcActivationResult';
// IMP-437 (D): 메모 저장/Suspend/Resume 실패 시 BE reason 코드 원문 노출 방지 — 기존 SSoT 확장 재사용.
import { LICENSE_DETAIL_ERROR_MESSAGES, resolveLicenseErrorMessage } from '../../../lib/licenseErrorMessages';
// RTSQA-339 (2026-05-14): .license-detail-modal-wide CSS 의존성 명시.
// admin.css 는 AdminLogin 진입 시 이미 import 되지만, 본 컴포넌트의 폭 확대
// 의존성을 컴포넌트 단위에서 명시적으로 잠금.
import '../../../asset/css/admin.css';

// IMP-424 (#359, IMP-423 후속): companyMap optional — 미전달 호출부는 companyMap={} 로
// 항상 miss 되어 dealerId/managerGroupId 모두 formatCompanyFallbackId truncate 로 표시된다
// (CompanyCell.js / LicenseDealerTab.js 와 동일 규약, 아래 dealerLabel 계산부 참조).
const LicenseDetailModal = ({ show, license, role, onClose, onSuccess, canOperate = true, companyMap = {} }) => {
    const dispatch = useDispatch();
    const callerCompanyId = useSelector((state) => state?.AuthReducer?.user?.companyId ?? null);
    // IMP-169 (AC-1): 분산된 권한 분기(role !== ORG_HQ)를 단일 변수로 통일.
    // canManage = 관리 권한(딜러/법인). HQ 는 view-only.
    // BUG-185 리뷰 Minor: canOperate=false 면 대행-쓰기(Memo/Suspend·Resume/DeactivatePc) 차단.
    // VoucherPrintModal 이중게이트와 대칭되는 defense-in-depth. 기본값 true 로 기존 호출부 무영향.
    const canManage = canOperate && role !== ORG_HQ;
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // PR #227 Round 1 review Minor #12: DDB sync 실패는 error 와 분리하여 정보성 경고로 표시.
    // BE MongoDB SSoT 는 성공했으나 rayteams gateway 동기화가 pending 인 케이스. error 는
    // 처리 자체가 실패한 경우만 사용.
    const [syncWarning, setSyncWarning] = useState(null);
    // Memo 편집 — Suspend/Resume 와 분리된 가벼운 메타데이터 편집.
    const [memo, setMemo] = useState('');
    const [memoSaving, setMemoSaving] = useState(false);
    const [memoMessage, setMemoMessage] = useState(null);
    // IMP-148: Activated PCs — PR #221 review #13/#15 (cross-license stale data + slice 공유 회귀)
    //   대응으로 local state 격리. SmartDentReducer.pcActivations 미사용 — PCActivationPanel 와
    //   격리되어 시나리오 (Modal A close → Modal B open 짧은 윈도우에서 stale 데이터로 IDOR 시도)
    //   가 방지된다. 응답 axios 직접 호출.
    const [pcActivations, setPcActivations] = useState([]);
    const [pcActivationLoading, setPcActivationLoading] = useState(false);
    const [pcActivationError, setPcActivationError] = useState(null);
    const [pcDeactivating, setPcDeactivating] = useState(null);
    const [pcMessage, setPcMessage] = useState(null);
    // IMP-388 FR-012: PC 비활성화 확인 모달 대기 rtid (null = 닫힘).
    const [pendingDeactivateRtid, setPendingDeactivateRtid] = useState(null);

    // FEAT-057: 정지·재개·메모·DDB 동기화·PC 조회는 이 라이선스 기록의 product 로 처리한다.
    // 서버는 product 없는 옛 문서를 smartdent 로 보므로 null(=SMARTDent)과 일치.
    // thunk 에는 원본 {key} 를 넘긴다 — 목록 밖 값이 null 로 바뀌어 SMARTDent 로 오인되지 않도록 thunk 가 직접 해석·거부한다.
    // 훅 규칙상 early return 앞에 두며, key 로 메모해 렌더마다 새 객체·warn 이 생기지 않게 한다.
    const licenseProductKey = license?.product;
    const product = useMemo(
        () => (licenseProductKey ? { key: licenseProductKey } : null),
        [licenseProductKey],
    );
    // FEAT-059: PC 활성화 미지원 제품(예: Surgical Guide)은 PC 조회·비활성화·이력 UI 를 쓰지 않는다.
    const pcEnabled = useMemo(
        () => isPcActivationEnabled(product, { handler: 'LicenseDetailModal.pcEnabled' }),
        [product],
    );

    // PR 사전 리뷰 Minor#1: resolvePcFailText 는 매핑 미스 시 내부에서 console.warn 을 찍는다
    //   (resolvePcFailMessage, IMP-421 §7). 이 모달은 메모 입력 등 자체 state 변경이 잦아
    //   렌더 본문에서 호출하면 키 입력마다 같은 warn 이 반복된다. 실패 1건당 1회로 되돌린다.
    const pcActivationErrorText = useMemo(
        () => (pcActivationError
            ? resolvePcFailText(pcActivationError, t, 'license.error.activeSeatsLoadFailed')
            : null),
        [pcActivationError],
    );

    // RTSQA-339 round 17 (사용자 요청 2026-05-14): Deactivate 성공·실패 알림이 15초 후
    // 자동 사라짐. 사용자 명시적 dismiss (닫기 버튼) 도 가능. PCActivationPanel 와 동일 패턴.
    useEffect(() => {
        if (!pcMessage) return undefined;
        const timerId = setTimeout(() => setPcMessage(null), 15000);
        return () => clearTimeout(timerId);
    }, [pcMessage]);
    // RTSQA-339 (2026-05-14, round 2 — 사용자 요청): Activation History 를 기본 확장
    // 으로 표시 + 토글 제거. modal 폭 90vw 확대 + table 자체 30vh 스크롤로 세로 부담
    // 흡수. modal open 시 logs 자동 fetch (Active Seats 와 동일 시점).
    // 'logs' local state 격리 — BUG-112 의 dispatchRedux: false 패턴 재사용.
    // Deactivate 성공 시 fresh fetch 트리거.
    const [pcActivationLogs, setPcActivationLogs] = useState([]);
    const [pcActivationLogsLoading, setPcActivationLogsLoading] = useState(false);
    const [pcActivationLogsError, setPcActivationLogsError] = useState(null);
    // PR #221 review #7: in-flight 동안 unmount 시 setState 차단 (PCActivationPanel 와 동일 패턴).
    // BUG-111 사용자 보고 (2026-05-12 / 2-3차): useCallback / handler closure 가 첫 mount
    // 의 mountedRef 객체를 capture → Strict Mode / useEffect 재실행 시 cleanup 으로 ref.current
    // = false 설정 후 후속 호출이 stale ref 검사로 setState 영구 무시 (Activated PCs 빈 표시,
    // Deactivate "Saving..." 영구 표시 회귀). React 18+ 가 unmount 후 setState 자동 무시하므로
    // mountedRef 패턴 제거. 모든 handler 에서 가드 없이 setState 호출.

    // BUG-111 (2026-05-12): 로컬 fetcher 가 deprecated BE 경로 (axios.get /pcs) 사용 중이었음.
    // BUG-108 에서 BE 엔드포인트 제거됨 → 404 → 빈 결과. PcActivationAction.GetPCActivations 가
    // rayteams gateway query 로 마이그레이션됐으나 본 컴포넌트는 SmartDentReducer slice 공유 회귀
    // 차단 (PR #221 review #13/#15) 목적의 local state 격리를 유지하므로 thunk 를 dispatch 하되
    // 반환값으로 로컬 state 갱신.
    //
    // 인자: voucherSk (lcuse: prefix) — 기존 licId (MongoDB _id) 와 형식 다름.
    //
    // BUG-112 (RTSQA-340, 2026-05-13): dispatchRedux: false 옵션 사용 — GetPCActivations 가
    // SmartDentReducer 의 pcActivations / activationCount 를 갱신해 PCActivationPanel
    // 카드의 'Active seats' / 'Activations' 값이 license.sk 기준으로 변경되는 회귀를 차단.
    // 본 컴포넌트는 local state (setPcActivations/setPcActivationLoading) 만 사용하므로
    // Redux mutation 일체 불필요.
    const fetchPcActivations = useCallback(async (voucherSk) => {
        if (!voucherSk) return;
        setPcActivationLoading(true);
        setPcActivationError(null);
        let result = null;
        let escapedError = null;
        try {
            result = await dispatch(PcActivationAction.GetPCActivations(voucherSk, { dispatchRedux: false, product }));
        } catch (e) {
            // BUG-111 review (Major #1): GetPCActivations 자체는 내부 catch 보유하지만
            // Redux middleware / 향후 코드 변경 시 예외가 escape 가능. error 정보를 사용자
            // 표시 + 개발자 추적 가능하도록 status/message 추출 (axios 패턴 유지).
            escapedError = e;
            result = null;
        }
        // BUG-111 사용자 보고 (2026-05-12 / 2번째): useCallback([dispatch]) 의 closure 가
        //   첫 mount 의 mountedRef 객체를 capture → Strict Mode / useEffect 재실행 시 cleanup
        //   에서 ref.current=false 설정되면 후속 호출도 stale ref 검사로 setState 영구 무시
        //   → pcActivations 가 [2 elements] 인데도 빈 상태로 유지되는 회귀.
        // React 18+ 는 unmount 후 setState 를 자동 무시하므로 mountedRef 가드 완전 제거.
        if (Array.isArray(result)) {
            setPcActivations(result);
        } else {
            // PR #214 Round 2 (Sec Major) 패턴 — error.message 200자 cap.
            const MAX_REASON_LEN = 200;
            const status = escapedError?.response?.status ?? null;
            const rawReason = typeof escapedError?.response?.data?.message === 'string'
                ? escapedError.response.data.message
                : (typeof escapedError?.message === 'string' ? escapedError.message : 'FETCH_FAILED');
            setPcActivationError({ status, reason: rawReason.slice(0, MAX_REASON_LEN) });
            setPcActivations([]);
        }
        setPcActivationLoading(false);
    }, [dispatch, product]);

    useEffect(() => {
        if (show) {
            setReason('');
            setError(null);
            // PR #240+ review (RTSQA-339 사전 리뷰 Minor): syncWarning 도 reset.
            // 다른 라이선스로 modal 재오픈 시 이전 DDB sync 경고 잔류 회귀 차단.
            setSyncWarning(null);
            setMemo(typeof license?.memo === 'string' ? license.memo : '');
            setMemoMessage(null);
            setPcMessage(null);
            // RTSQA-339 round 2: history state reset (fresh fetch 보장).
            setPcActivationLogs([]);
            setPcActivationLogsError(null);
        }
    }, [show, license]);

    // RTSQA-339 round 2 (사용자 요청 2026-05-14): history 를 기본 확장 표시. modal
    // open + license.sk 변경 시 logs 자동 fetch (Active Seats 와 같은 시점). lazy
    // 토글 패턴 제거 — table 자체 30vh 스크롤 + modal 90vw 폭으로 세로 부담 흡수.
    // dispatchRedux: false + withLogs: true 로 BUG-112 의 slice 격리 패턴 재사용.
    const fetchActivationLogs = useCallback(async (voucherSk) => {
        if (!voucherSk) return;
        setPcActivationLogsLoading(true);
        setPcActivationLogsError(null);
        let result = null;
        let escapedError = null;
        try {
            result = await dispatch(
                PcActivationAction.GetPCActivations(voucherSk, { dispatchRedux: false, withLogs: true, product }),
            );
        } catch (e) {
            escapedError = e;
            result = null;
        }
        if (result && Array.isArray(result.logs)) {
            setPcActivationLogs(result.logs);
        } else {
            const MAX_REASON_LEN = 200;
            const status = escapedError?.response?.status ?? null;
            const rawReason = typeof escapedError?.response?.data?.message === 'string'
                ? escapedError.response.data.message
                : (typeof escapedError?.message === 'string' ? escapedError.message : 'FETCH_FAILED');
            setPcActivationLogsError({ status, reason: rawReason.slice(0, MAX_REASON_LEN) });
            setPcActivationLogs([]);
        }
        setPcActivationLogsLoading(false);
    }, [dispatch, product]);

    // IMP-148: 모달 마운트(open) + license 변경 시 활성 PC 목록 재조회. 닫힐 때 reset.
    // BUG-111: license._id (MongoDB) → license.sk (rayteams-license DDB query key) 로 변경.
    // RTSQA-339 round 2: activation logs 도 같은 시점에 fetch (history 기본 표시).
    useEffect(() => {
        if (!show || !license?.sk || !pcEnabled) {
            setPcActivations([]);
            setPcActivationError(null);
            setPcActivationLogs([]);
            setPcActivationLogsError(null);
            return;
        }
        fetchPcActivations(license.sk);
        fetchActivationLogs(license.sk);
    }, [show, license?.sk, pcEnabled, fetchPcActivations, fetchActivationLogs]);

    if (!license) return null;

    // FEAT-032: voucher_license.status('suspend') 또는 기존 DDB suspended(boolean) 양쪽 지원.
    const isSuspended = license.status === 'suspend' || license.suspended === true;
    // IMP-150 + PR #215 review (CQ Suggestion / Sec Suggestion):
    //   primary  : groupName 우선 (사용자/그룹명) → userid → userId.
    //   secondary: email (있을 때 별도 줄로 표시).
    //   - whitespace-only 문자열 거부 (.trim() 가드 — userEmail 과 정책 비대칭 해소).
    //   - sk fallback 제거 — 내부 schema 노출 방지 (별도 'QR SK' 행에서 명시 표시 중).
    // IMP-153: friendlyName/userEmail 은 licenseHelper 헬퍼로 추출. trimmed 로컬 함수는
    //   아래 userPhone(FEAT-032) 전용으로만 잔류 — phone 헬퍼화는 IMP-153 미대상(FEAT-032 영역).
    const trimmed = (s) => (typeof s === 'string' && s.trim() ? s.trim() : null);
    const friendlyName = getFriendlyName(license);
    const userEmail = getUserEmail(license);
    // FEAT-032 (BE PR #118, 2026-05-14): voucher_license.phoneNumber 필드 추가.
    // License Info 표에 Phone 행 신규 표시.
    const userPhone = trimmed(license.phoneNumber);
    // IMP-424: IMP-423 은 PC 활성화 패널 경로(resolveOtherLicenseInfo)만 "회사명 (ID)" 표기로
    // 고쳤고 본 모달은 별도 렌더 경로라 raw ID 만 노출되던 결함을 해소.
    // PR #537 리뷰 재지적: dealerId/managerGroupId 분기가 각자 다른 규칙을 쓰던 것을
    // CompanyCell.js / LicenseDealerTab.js 의 확립된 규약 하나로 통일한다 —
    // 이름 매칭 성공 → raw id 유지 / 실패 → formatCompanyFallbackId truncate.
    // dealerId 우선, 없으면 managerGroupId 폴백. 이름 조회는 항상 원본 id 로 수행(truncate 전).
    // companyMap 은 companyId 키라 managerGroupId(rayteams-manager-group _id)로는 실질적으로
    // 항상 miss → 이 분기는 사실상 truncate-only 로 동작하지만, 규칙을 단일 경로로 유지하고
    // 향후 키 공간 통일 대비를 위해 이름 조회 limb 을 남겨둔다.
    const rawDealerId = license.dealerId || license.managerGroupId || null;
    const dealerName = rawDealerId ? (companyMap?.[rawDealerId]?.name || null) : null;
    const displayDealerId = dealerName ? rawDealerId : formatCompanyFallbackId(rawDealerId);
    const dealerLabel = rawDealerId ? formatCompanyInlineLabel(displayDealerId, dealerName) : '-';
    const expiresLabel = formatLicenseDate(license.expired);
    const issuedAtLabel = formatLicenseDate(license.created ?? license.issuedAt);
    let statusKey = 'Active';
    if (isSuspended) statusKey = 'Suspended';
    else if (isExpired(license)) statusKey = 'Expired';
    const suspendHistory = Array.isArray(license.suspendHistory) ? license.suspendHistory : [];
    // RTSQA-339 (2026-05-14 round 2, 사용자 요청): License Info 표에 Type 행 추가.
    // CustomerLicensesTable 와 동일 패턴 — isRenewalLicense 헬퍼 SSoT 사용.
    const typeLabel = isRenewalLicense(license) ? t('license.label.renewal') : t('license.label.initial');

    const initialMemo = typeof license?.memo === 'string' ? license.memo : '';
    const memoDirty = memo !== initialMemo;
    const memoTooLong = memo.length > MEMO_MAX_LEN;

    const handleSaveMemo = async () => {
        if (!memoDirty || memoTooLong) return;
        setError(null);
        setMemoMessage(null);
        setMemoSaving(true);
        try {
            const result = await dispatch(SmartDentAction.UpdateLicenseMemo({
                _id: license._id,
                sk: license.sk,
                memo,
                currentRecord: license,
                product,
            }));
            if (result?.ok === false) {
                setError(resolveLicenseErrorMessage(
                    LICENSE_DETAIL_ERROR_MESSAGES, result.reason, null, t,
                    t('common.message.error.processingFailed'),
                ));
            } else {
                setMemoMessage(t('common.message.success.memoSaved'));
            }
        } finally {
            setMemoSaving(false);
        }
    };

    // IMP-148: 원격 PC 비활성화.
    // 권한 가드: ORG_HQ 차단 (FE defense-in-depth) + BE IDOR 가드(getAllowedDealerIds) 1차 방어.
    // PR #221 review:
    //   - #2: defense-in-depth — handleDeactivatePc 내부에서도 role gate.
    //   - #14: 응답 해석 — `result?.ok` true 만 성공 (PCActivationPanel 와 일관).
    //   - #18: window.confirm fail-closed — 미지원 환경에서 비활성화 자체 차단 (파괴적 동작).
    // IMP-388 FR-012: window.confirm → ModalConfirm. 행 버튼은 requestDeactivatePc 로 확인 모달만 열고,
    //   확인 버튼만 handleDeactivatePc 를 실행 (닫기/배경/ESC 는 취소 — fail-closed).
    const requestDeactivatePc = (rtid) => {
        if (!rtid || !license?._id || !license?.sk) return;
        if (!canManage || !pcEnabled) return;
        if (pcDeactivating) return;
        setPendingDeactivateRtid(rtid);
    };
    const handleDeactivatePc = async (rtid) => {
        if (!rtid || !license?._id || !license?.sk) return;
        if (!canManage || !pcEnabled) return;
        setPcDeactivating(rtid);
        setPcMessage(null);
        try {
            // PR #241 review (CQ Major #1): BUG-111 마이그레이션 이후 licId 미사용 →
            // dead code 제거. DeactivatePc 는 license.sk 직접 사용.
            // BUG-109: DeactivatePc 가 rayteams gateway 경유로 변경됨.
            // BUG-111: used[] 모델 — DeactivatePc(voucherSk, rtid). voucherSk = license.sk.
            const result = await dispatch(PcActivationAction.DeactivatePc(license.sk, rtid, product));
            if (result?.ok) {
                setPcMessage({ type: 'success', text: t('pcActivation.deactivateSuccess') });
                // 재조회 — local state 갱신 (slice 공유 회피).
                // BUG-111: license.sk 로 호출 (voucherSk 모델, _id 아님).
                fetchPcActivations(license.sk);
                // RTSQA-339 round 2: history 가 기본 표시이므로 logs 도 즉시 재 fetch.
                // DeactivatePc 가 BE 의 activationLogs 에 append 한 결과 반영.
                fetchActivationLogs(license.sk);
                // BUG-114 (2026-05-19): PCActivationPanel 의 Redux state (pcActivations /
                // activationCount) 동기 갱신. fetchPcActivations 는 BUG-112 dispatchRedux:false
                // 패턴이라 local state 만 갱신 → 같은 화면의 PCActivationPanel 카운트가 stale
                // 유지되는 회귀 차단. 옵션 없이 호출 = dispatchRedux:true 기본 → Redux mutation.
                //
                // 주의 (PR #246 review Minor — IMP-210 known-debt):
                //   matchedVoucher.sk !== license.sk 인 cross-voucher 시나리오 (예: initial
                //   활성 + renewal Deactivate) 에서 Redux 단일 슬롯이 license.sk 기준으로
                //   교체되어 PCActivationPanel 이 일시적으로 잘못된 데이터 표시.
                //   복구 경로: 직후 onSuccess?.() cascade → dealerCustomerLicenses 갱신 →
                //   matchedVoucher 변경 → PCActivationPanel.useEffect 의 GetPCActivations
                //   재호출로 자가치유. 즉 onSuccess 의 결과적 부수 효과.
                //   실제 발생 빈도 낮음 + 자가치유 보장. 별도 IMP (service layer + voucher
                //   별 slice 분리) 시 본 dispatch 제거 또는 가드 추가 예정.
                dispatch(PcActivationAction.GetPCActivations(license.sk, { product }));
                // PR #221 review #1: parent (LicenseHub/Tab) 의 license 목록도 stale 가능 →
                //   onSuccess?.() 로 refetch 트리거.
                // BUG-114 부수 효과 (위 주석 참조): cross-voucher 자가치유 경로 트리거.
                onSuccess?.();
            } else {
                // IMP-421: reason 원문 노출 → 안내 문구. 매핑 부재 시 기존 일반 안내 유지.
                setPcMessage({
                    type: 'danger',
                    text: resolvePcFailText(result, t, 'common.message.error.processingFailed'),
                });
            }
        } finally {
            setPcDeactivating(null);
        }
    };

    const handleAction = async (actionType) => {
        setError(null);
        setSyncWarning(null);
        setLoading(true);
        try {
            // FEAT-032: MongoDB _id 만 전달 (sk 제거). BE 가 voucher_license._id 기준 처리.
            const licId = license._id?.toString?.() ?? license._id;
            const thunk = actionType === 'suspend'
                ? SmartDentAction.SuspendLicense({ _id: licId, reason, product })
                : SmartDentAction.ResumeLicense({ _id: licId, reason, product });

            const result = await dispatch(thunk);
            if (result?.ok === false) {
                setError(resolveLicenseErrorMessage(
                    LICENSE_DETAIL_ERROR_MESSAGES, result.reason, null, t,
                    t('common.message.error.processingFailed'),
                ));
                return;
            }
            // BUG-109: BE MongoDB 갱신 성공 후 rayteams gateway 로 DDB 동기화.
            // 실패해도 MongoDB 가 SSoT — 정보성 경고(setSyncWarning)로 분리하여 error 와
            // 시각적으로 구분 (PR #227 Round 1 review Minor #12 UX 혼선 해소). onSuccess 는
            // 부모 컴포넌트의 목록 갱신을 위해 호출 — 사용자는 경고를 보면서도 변경된 상태를
            // 확인 가능.
            const voucherSk = result?.record?.sk;
            if (typeof voucherSk === 'string' && voucherSk.length > 0) {
                const ddbProduct = resolveLicenseProduct(product, { handler: 'LicenseDetailModal.syncDdb' });
                if (!ddbProduct) {
                    // 목록 밖 product: 잘못된 licenseAppId 로 쓰지 않도록 DDB 동기화를 건너뛴다.
                    console.warn('[guard] voucher license DDB sync skipped', {
                        handler: 'LicenseDetailModal.syncDdb',
                        callerCompanyId,
                        target: { sk: voucherSk, product: license.product },
                        reason: 'license product is not in LICENSE_PRODUCTS',
                    });
                    setSyncWarning(t('pcActivation.ddbSyncPending'));
                } else {
                    const ddbThunk = actionType === 'suspend'
                        ? PcActivationAction.SuspendVoucherLicenseDdb(voucherSk, ddbProduct)
                        : PcActivationAction.ResumeVoucherLicenseDdb(voucherSk, ddbProduct);
                    const ddbResult = await dispatch(ddbThunk);
                    if (ddbResult?.ok === false) {
                        setSyncWarning(t('pcActivation.ddbSyncPending'));
                    }
                }
            } else {
                // PR #227 Round 1 review Major #5: BE 응답에 sk 가 없으면 DDB 동기화를 시도조차
                // 하지 못함 — 정합성 위험을 사용자에게 명시적으로 노출.
                // RTSQA-339 round 20 review (사전 리뷰 Major): console.warn 제거 —
                // setSyncWarning 이 동일 상황을 사용자에게 이미 전달. 내부 메서드명/상태 누출 차단.
                setSyncWarning(t('pcActivation.ddbSyncPending'));
            }
            onSuccess?.();
        } finally {
            setLoading(false);
        }
    };

    const cancelDeactivateConfirm = () => setPendingDeactivateRtid(null);
    const confirmDeactivateConfirm = () => {
        const rtid = pendingDeactivateRtid;
        setPendingDeactivateRtid(null);
        if (rtid && !pcDeactivating) handleDeactivatePc(rtid);
    };

    return (
        <>
        {/* IMP-179: size="xl" 로 폭을 넓혀 좌/우 2-column 레이아웃 적용.
        기존 단일 column 으로 인한 세로 스크롤을 줄여 한 화면에서 모든 섹션 가시.
        RTSQA-339 (2026-05-14): activation history 추가 + Memo/Status Action 좌측
        이동에 따른 정보 밀도 증가 — size xl (1140px) 위에 dialogClassName 으로
        max-width: 90vw 확대 (admin.css 의 .license-detail-modal-wide).
        RTSQA-339 round 4 (2026-05-14, 사용자 요청): scrollable=true — modal 외곽
        (window-level) 스크롤 제거. modal-dialog 가 viewport 에 fit 되고, 콘텐츠가
        넘치면 modal-body 내부에서만 스크롤 (admin.css 의 max-height: 90vh + body
        overflow). 사용자 요구 "스크롤이 안 생기게 크기를 조정".
        */}
        <Modal
            show={show}
            onHide={onClose}
            size="xl"
            dialogClassName="license-detail-modal-wide"
            scrollable
        >
            <Modal.Header className="d-flex justify-content-between align-items-center">
                {/* IMP-179: 제목 가시성 강화 — h5 → h4 (modal-title 시각 우위 확보) */}
                <h4 className="modal-title mb-0">{t('license.label.detail')}</h4>
                <button
                    type="button"
                    className="btn-close"
                    aria-label={t('common.button.close')}
                    onClick={onClose}
                />
            </Modal.Header>
            <Modal.Body className="p-3">
                {error && (
                    <div className="alert alert-danger py-2">{error}</div>
                )}
                {/* BUG-109: DDB sync pending 정보성 경고 — error 와 분리 (warning 색상). */}
                {syncWarning && (
                    <div className="alert alert-warning py-2">{syncWarning}</div>
                )}

                {/* IMP-179: 2-column 레이아웃 — 좌측 (license info table + suspend history)
                    / 우측 (activated PCs + memo + status action). 섹션 간 세로 누적 → 좌우 분산. */}
                <div className="row g-3">
                    <div className={pcEnabled ? 'col-lg-6' : 'col-12'}>
                <table className="table table-sm mb-3">
                    <tbody>
                        {/* IMP-192 (사용자 요청 2026-05-12): col-lg-6 의 좁은 폭에서 라벨이
                            2줄로 줄바꿈되는 회귀 → 모든 th 에 nowrap. 값 셀은 timestamp 만
                            nowrap (긴 sk 는 그대로 wrap 허용 — text-break). */}
                        <tr>
                            <th style={{ whiteSpace: 'nowrap' }}>{t('user.field.userId')}</th>
                            <td>
                                <div>{friendlyName}</div>
                                {userEmail && <div className="small text-muted">{userEmail}</div>}
                            </td>
                        </tr>
                        <tr><th style={{ whiteSpace: 'nowrap' }}>{t('license.label.dealer')}</th><td>{dealerLabel}</td></tr>
                        {/* RTSQA-339 round 2: License Type (Initial / Renewal) — isRenewalLicense SSoT 헬퍼. */}
                        <tr><th style={{ whiteSpace: 'nowrap' }}>{t('common.field.type')}</th><td>{typeLabel}</td></tr>
                        {/* FEAT-032 (BE PR #118): Phone 행 — 다른 행 (Dealer/Type/Expires) 과
                            동일하게 무조건 표시. 신규 데이터만 phoneNumber 보유 — 부재 시 '-'. */}
                        <tr><th style={{ whiteSpace: 'nowrap' }}>{t('common.field.phone')}</th><td>{userPhone || '-'}</td></tr>
                        <tr><th style={{ whiteSpace: 'nowrap' }}>{t('common.label.expires')}</th><td style={{ whiteSpace: 'nowrap' }}>{expiresLabel}</td></tr>
                        <tr><th style={{ whiteSpace: 'nowrap' }}>{t('license.label.issuedAt')}</th><td style={{ whiteSpace: 'nowrap' }}>{issuedAtLabel}</td></tr>
                        <tr><th style={{ whiteSpace: 'nowrap' }}>{t('common.label.status')}</th><td>{t(statusKey)}</td></tr>
                        {license.sk && (
                            <tr><th style={{ whiteSpace: 'nowrap' }}>{t('license.field.qrSk')}</th><td className="text-break small">{license.sk}</td></tr>
                        )}
                    </tbody>
                </table>

                {/* suspendHistory 이벤트 목록.
                    RTSQA-339 round 3 (사용자 요청 2026-05-14): 섹션 헤더 한 단계 확대
                    (h6 → h5). modal 폭 확대 + 정보 밀도 증가에 따른 가독성 보강. */}
                {suspendHistory.length > 0 && (
                    <div className="mb-3">
                        <h5 className="mb-2">{t('license.label.suspendHistory')}</h5>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>{t('common.label.action')}</th>
                                        <th>{t('common.label.at')}</th>
                                        <th>{t('common.label.by')}</th>
                                        <th>{t('common.field.reason')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {suspendHistory.map((evt, i) => (
                                        <tr key={i}>
                                            <td>{evt.action || '-'}</td>
                                            {/* IMP-438: SSoT(formatLicenseDate) 로 교체 — 이미 '-' 폴백을 포함하므로 삼항 제거. */}
                                            <td>{formatLicenseDate(evt.at)}</td>
                                            <td>{evt.actor || evt.by || '-'}</td>
                                            <td>{evt.reason || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* RTSQA-339 (2026-05-14): Memo + Status Action 을 좌측으로 이동.
                    history 길이 흡수를 위해 우측은 Active Seats + Activation History 전용. */}

                {/* Memo 섹션 — 운영용 메모 (Suspend/Resume 과 무관, 별도 Save 버튼).
                    Suspend/Resume 같은 상태 전이가 아니므로 audit history 미기록.
                    !canManage(=HQ) 시 편집 차단(아래 읽기 전용 표시). */}
                {canManage && (
                    <div className="mb-3 pt-2 border-top">
                        {/* IMP-192 (2026-05-12): 섹션 헤더 스타일을 Activated PCs 와 통일.
                            RTSQA-339 round 3 (2026-05-14): h6 → h5 (한 단계 확대). */}
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h5 className="mb-0">{t('common.field.memo')}</h5>
                            <small className={memoTooLong ? 'text-danger' : 'text-muted'}>
                                {memo.length}/{MEMO_MAX_LEN}
                            </small>
                        </div>
                        {/* RTSQA-339 round 13 (사용자 보고 2026-05-14): CSS Grid 로 정렬 정확성
                            보강. flex 는 box-sizing/padding/font metric 차이로 button 폭이
                            grid-template 보다 작게 렌더될 수 있음. grid-template-columns:
                            1fr 8rem 으로 픽셀 단위 정확 분리. align-items: center 로 button
                            이 textarea 의 수직 중앙. 두 영역 (Memo + Reason) 동일 grid →
                            button 좌측/우측 가장자리 픽셀 단위 일치. */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 8rem',
                                gap: '0.5rem',
                                alignItems: 'center',
                            }}
                        >
                            <textarea
                                className={`form-control form-control-sm${memoTooLong ? ' is-invalid' : ''}`}
                                rows={3}
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                placeholder={t('common.placeholder.operationalMemoOptional')}
                                disabled={memoSaving}
                                style={{ minWidth: 0 }}
                            />
                            <button
                                className="btn btn-outline-primary btn-sm"
                                style={{ whiteSpace: 'nowrap', width: '100%', margin: '0 0 0 1px'}}
                                onClick={handleSaveMemo}
                                disabled={!memoDirty || memoTooLong || memoSaving}
                            >
                                {memoSaving ? t('common.status.saving') : t('user.button.saveMemo')}
                            </button>
                        </div>
                        {memoMessage && (
                            <div className="text-success small mt-1">✓ {memoMessage}</div>
                        )}
                    </div>
                )}
                {!canManage && typeof license.memo === 'string' && license.memo.trim().length > 0 && (
                    <div className="mb-3 pt-2 border-top">
                        <h5 className="mb-2">{t('common.field.memo')}</h5>
                        <div className="small text-muted" style={{ whiteSpace: 'pre-wrap' }}>{license.memo}</div>
                    </div>
                )}

                {/* 중지/재개 액션 (role이 HQ가 아닌 경우).
                    Suspend 는 사유 필수(최소 10자) — 감사 추적 + 분쟁 시 근거 확보.
                    Resume 은 사유 선택 (재개는 일반적 정상화이므로 강제 X). */}
                {canManage && (() => {
                    const REASON_MIN = 10;
                    const trimmedReason = (reason || '').trim();
                    const reasonValid = trimmedReason.length >= REASON_MIN;
                    return (
                    <div className="pt-2 border-top">
                        <h5 className="mb-2">{t('service.label.statusAction')}</h5>
                        <div className="alert alert-info py-2 small mb-2">
                            {t('license.message.suspendNoExtend')}
                        </div>
                        {/* RTSQA-339 round 5 (사용자 요청 2026-05-14): Reason input 우측에
                            Suspend/Resume 버튼 input-group 배치 — 세로 단축으로 modal
                            스크롤 회피. h6 라벨은 input-group 위에 유지 (sub-heading 의미). */}
                        <div className="mb-2">
                            {/* RTSQA-339 round 3: Reason 은 Status Action 의 sub-heading 으로 h6 유지. */}
                            <h6 className="mb-2">
                                {t('common.field.reason')}
                                <RequiredMark required={!isSuspended} />
                            </h6>
                            {/* RTSQA-339 round 13 (사용자 보고 2026-05-14): Memo 영역과 정확히
                                동일한 CSS Grid 패턴 — grid-template-columns: 1fr 8rem 로
                                button x 좌표 픽셀 단위 일치. */}
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 8rem',
                                    gap: '0.5rem',
                                    alignItems: 'center',
                                }}
                            >
                                <input
                                    className="form-control form-control-sm"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder={isSuspended
                                        ? t('common.placeholder.enterReasonOptional')
                                        : t('common.placeholder.enterReasonRequired10Chars')}
                                    aria-required={!isSuspended}
                                    style={{ minWidth: 0 }}
                                />
                                {!isSuspended && (
                                    <button
                                        className="btn btn-outline-danger btn-sm"
                                        style={{ whiteSpace: 'nowrap', width: '100%', margin: '0 0 0 1px'}}
                                        onClick={() => handleAction('suspend')}
                                        disabled={loading || !reasonValid}
                                        title={reasonValid
                                            ? ''
                                            : t('common.message.error.reasonMin10Chars')}
                                    >
                                        {t('license.button.suspend')}
                                    </button>
                                )}
                                {isSuspended && (
                                    <button
                                        className="btn btn-primary btn-sm"
                                        style={{ whiteSpace: 'nowrap', width: '100%', margin: '0 0 0 1px'}}
                                        onClick={() => handleAction('resume')}
                                        disabled={loading}
                                    >
                                        {t('license.button.resume')}
                                    </button>
                                )}
                            </div>
                            {!isSuspended && !reasonValid && trimmedReason.length > 0 && (
                                <div className="form-text text-danger small">
                                    {t('common.message.error.reasonMin10Chars')}
                                </div>
                            )}
                        </div>
                    </div>
                    );
                })()}
                    </div>
                    {pcEnabled && (
                    <div className="col-lg-6">

                {/* IMP-148: Activated PCs 섹션 — local state (slice 격리, PR #221 review #13/#15).
                    DeactivatePc 만 thunk 재사용 (응답 해석은 PCActivationPanel 와 일관 — review #14).
                    RTSQA-339 round 3 (2026-05-14): h6 → h5 (한 단계 확대). */}
                <div className="mb-3">
                    <h5 className="mb-2">{t('pcActivation.activeSeats')}</h5>
                    {/* RTSQA-339 round 17 (2026-05-14): 15초 자동 dismiss + 닫기 버튼. */}
                    {pcMessage && (
                        <div
                            className={`alert alert-${pcMessage.type} alert-dismissible py-1 small mb-2`}
                            role="alert"
                        >
                            {pcMessage.text}
                            <button
                                type="button"
                                className="btn-close"
                                aria-label={t('common.button.close')}
                                onClick={() => setPcMessage(null)}
                                style={{ fontSize: '0.65rem' }}
                            />
                        </div>
                    )}
                    {pcActivationLoading ? (
                        <div className="text-muted small">
                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                            {t('common.status.loading')}
                        </div>
                    ) : pcActivationError ? (
                        /* PR #221 review #10/#11: error 분기 명시 — 빈 배열로 false-negative 회피.
                           network/403/5xx 등을 "PC 없음" 으로 잘못 표시하지 않음. */
                        <div className="alert alert-danger py-1 small mb-0">
                            {pcActivationErrorText}
                        </div>
                    ) : pcActivations.length === 0 ? (
                        <div className="text-muted small">{t('pcActivation.noActivatedPcs')}</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered table-xs mb-0">
                                <thead>
                                    <tr>
                                        <th>{t('common.label.pc')}</th>
                                        {/* RTSQA-339 round 10 (사용자 요청 2026-05-14): Active Seats 에도
                                            License Type 컬럼 추가. modal 단일 라이선스이므로 모든 row 가
                                            동일 값 (typeLabel — isRenewalLicense SK 우선 판정 SSoT). */}
                                        <th style={{ whiteSpace: 'nowrap' }}>{t('pcActivation.historyColLicenseType')}</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>{t('license.label.activatedAt')}</th>
                                        <th>{t('common.label.status')}</th>
                                        {canManage && <th>{t('common.label.actions')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pcActivations.map((pc) => {
                                        // PR #221 review #12: pc._id fallback 제거 — rtid 없는 행은 표시 안 함.
                                        //   BE 응답에 rtid 가 누락된 경우 MongoDB ObjectId 가 deactivate 인자로
                                        //   잘못 전달되는 위험 차단.
                                        // BUG-111 (2026-05-12): pc.guid → pc.rtid (used[] 모델). pc.activatedAt
                                        //   → pc.created, pc.suspended → pc.revoke.
                                        // BUG-111 review (Minor #2/#3): 변수명 정합 (rtid) + activatedAt fallback
                                        //   제거 — used[] 모델에서는 created 필드가 보장되며 activatedAt 미존재.
                                        if (typeof pc?.rtid !== 'string' || !pc.rtid) return null;
                                        const rtid = pc.rtid;
                                        // PR #221 review #17: title 에도 단축값만 표시 — hover/스크린리더/
                                        //   context menu 에서 full rtid 가 추출되는 표면 제거.
                                        // IMP-171: 인라인 마스킹 → maskPcGuid 헬퍼 (동작 동일).
                                        const rtidShort = maskPcGuid(rtid);
                                        const activatedAtLabel = formatLicenseDate(pc.created);
                                        const suspended = pc.revoke === true;
                                        return (
                                            <tr key={rtid} className="align-middle">
                                                {/* IMP-192 (2026-05-12): rtid 는 사용자 식별 불가 → pcinfo.hostname/localip
                                                    표시. pcinfo 부재 시 rtidShort fallback (legacy/응답 누락 방어).
                                                    title 은 PR #221 review #17 정책대로 rtidShort 만 (full rtid 표면화 차단). */}
                                                <td className="small" title={rtidShort}>
                                                    {pc.pcinfo?.hostname ? (
                                                        <>
                                                            <div>{pc.pcinfo.hostname}</div>
                                                            {pc.pcinfo?.localip && (
                                                                <div className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>
                                                                    {pc.pcinfo.localip}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="font-monospace">{rtidShort}</span>
                                                    )}
                                                </td>
                                                {/* RTSQA-339 round 10: License Type — modal 단일 라이선스이므로
                                                    모든 row 동일값. */}
                                                <td className="small">{typeLabel}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{activatedAtLabel}</td>
                                                <td>{suspended ? t('license.status.suspended') : t('license.status.active')}</td>
                                                {canManage && (
                                                    <td>
                                                        {!suspended && (
                                                            <button
                                                                className="btn btn-sm btn-outline-danger py-0"
                                                                style={{ fontSize: '0.75rem', margin: '0 0 0 1px'}}
                                                                onClick={() => requestDeactivatePc(rtid)}
                                                                disabled={pcDeactivating === rtid}
                                                            >
                                                                {pcDeactivating === rtid
                                                                    ? t('common.status.saving')
                                                                    : t('pcActivation.deactivateBtn')}
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* RTSQA-339 (2026-05-14, round 2 — 사용자 요청): Activation History 기본
                    확장 표시 (토글 제거). modal 폭 90vw + 테이블 내부 30vh 스크롤로 세로
                    부담 흡수. history 는 모든 role 에 read-only — Deactivate 버튼만
                    ORG_HQ 제외 (기존 정책 유지, Active Seats 영역에서 처리). */}
                <div className="mt-3 pt-2 border-top">
                    <h5 className="mb-2">
                        {t('pcActivation.activationHistory')}
                        {pcActivationLogs.length > 0 && (
                            <span className="badge bg-light text-dark ms-2">
                                {pcActivationLogs.length}
                            </span>
                        )}
                    </h5>
                    <PCActivationHistoryTable
                        logs={pcActivationLogs}
                        loading={pcActivationLogsLoading}
                        error={pcActivationLogsError}
                        licenseTypeLabel={typeLabel}
                        resolveActorLabel={(rawActor) => {
                            // RTSQA-339 round 19+20 (2026-05-14): activationLogs[].actor 는 userId.
                            // userid/userId 이중체크를 getLicenseUserId SSoT 헬퍼로 통합(IMP-229).
                            // 매칭되면 groupName / email 로 enrichment. 외부 사용자 actor 또는
                            // license groupName/email 부재 시 raw 그대로 (fallback).
                            if (!rawActor) return rawActor;
                            const licenseUserId = getLicenseUserId(license);
                            if (rawActor === licenseUserId) {
                                return license?.groupName || license?.email || rawActor;
                            }
                            return rawActor;
                        }}
                    />
                </div>
                    </div>
                    )}
                </div> {/* IMP-179: /row */}
            </Modal.Body>
        </Modal>
        <ModalConfirm
            info={{
                showModal: show && pcEnabled && !!pendingDeactivateRtid,
                title: t('pcActivation.deactivateBtn'),
                content: t('pcActivation.deactivatePcConfirm'),
                actions: [{ title: t('common.button.confirm'), callback: confirmDeactivateConfirm, css: 'btn btn-block btn-danger confirm-danger' }],
            }}
            callbackClose={cancelDeactivateConfirm}
        />
        </>
    );
};

export default LicenseDetailModal;
